import { useContext, useMemo } from 'react'
import { AuthContext } from '@/contexts/AuthContext'
import { registraPermesso } from '@/lib/raccoltaPermessi'

interface UserPermissions {
  codice: string
  nome: string
  categoria: string
}

interface UsePermissionsReturn {
  permissions: UserPermissions[]
  hasPermission: (permissionCode: string) => boolean
  loading: boolean
  error: string | null
}

/**
 * Hook per gestire i permessi dell'utente corrente.
 *
 * Legge da AuthContext, unica fonte dei permessi lato client: prima questo
 * hook faceva una GET /permissions/me propria a ogni mount, e i suoi
 * consumatori (PermissionRoute in testa) potevano vedere un insieme di
 * permessi diverso dal resto della UI.
 *
 * La firma resta { permissions, hasPermission, loading, error } per non
 * toccare i consumatori. AuthContext espone i soli codici, quindi `nome` e
 * `categoria` arrivano vuoti: nessun consumatore legge quel campo, e per
 * l'anagrafica completa dei permessi la fonte e' GET /permissions/matrix.
 */
export const usePermissions = (): UsePermissionsReturn => {
  const authContext = useContext(AuthContext)
  const codici = authContext?.permissions

  const permissions = useMemo<UserPermissions[]>(
    () => (codici ?? []).map(codice => ({ codice, nome: '', categoria: '' })),
    [codici],
  )

  const hasPermission = (permissionCode: string): boolean => {
    registraPermesso(permissionCode)
    return (codici ?? []).includes(permissionCode)
  }

  return {
    permissions,
    hasPermission,
    loading: authContext?.loading ?? true,
    // AuthContext non espone un errore: inghiotte il fallimento di
    // /permissions/me lasciando l'array vuoto. Restituire null e' meno
    // informativo di prima, ma non inventa uno stato che non esiste.
    error: null,
  }
}

export default usePermissions
